import { useMemo, useState } from 'react'
import { GroupingPanel } from './GroupingPanel.js'
import type { SampleBucket } from '../OperationTypes.js'
import type { VisualizationPipeline } from '../RunPipeline.js'
import type { VisualizationSampleContainer } from '../VisualizationSampleContainer.js'
import type { MetadataFields } from '../MetadataFields.js'

type Props = {
  samples: VisualizationSampleContainer[]
  initialPipeline?: VisualizationPipeline
  initialSelectedKeys?: Record<string, true>
  onChange?: (state: { pipeline: VisualizationPipeline; selectedBuckets: SampleBucket[]; selectedBucketKeys: Record<string, true> }) => void
  onFieldsChange?: (metadataFields: MetadataFields) => void
  onPipelineChange?: (pipeline: VisualizationPipeline) => void
  classNames?: string
}

const defaultPipeline: VisualizationPipeline = {
  filters: [],
  groupBys: [],
  projections: [],
  sortBys: []
} as const

export function SelectableGroupingWrapper({ samples, initialPipeline, initialSelectedKeys, onChange, onFieldsChange, classNames }: Props) {
  const [allBuckets, setAllBuckets] = useState<SampleBucket[]>([])

  const pipeline = useMemo<VisualizationPipeline>(() => initialPipeline ?? defaultPipeline, [initialPipeline])
  const [currentPipeline, setCurrentPipeline] = useState<VisualizationPipeline>(pipeline)
  const [selectedKeys, setSelectedKeys] = useState<Record<string, true>>(initialSelectedKeys ?? {})
  const [, setMetadataFields] = useState<MetadataFields | undefined>(undefined)

  const selectedBuckets = (buckets: SampleBucket[]) => buckets.filter((b) => (selectedKeys ?? {})[b.key]) //, [allBuckets, selectedKeys])

  const toggleKey = (key: string) => {
    if (selectedKeys) {
      if (selectedKeys[key]) {
        const tmp = { ...selectedKeys }
        delete tmp[key]
        setSelectedKeys(tmp)
      } else {
        setSelectedKeys({ ...selectedKeys, [key]: true })
      }
    }
  }

  const handleBucketsChange = (buckets: SampleBucket[]) => {
    setAllBuckets(buckets)

    if (
      Object.keys(selectedKeys ?? {}).length === 0 ||
      !buckets.map((b) => b.key).some((b) => [...Object.keys(selectedKeys ?? {})].includes(b))
    ) {
      const record: Record<string, true> = {}
      buckets.slice(0, 7).forEach((b) => (record[b.key] = true))
      setSelectedKeys(record)
    }
  }

  const applyChanges = (pipeline: VisualizationPipeline, buckets: SampleBucket[], selectedBucketKeys: Record<string, true>) => {
    onChange?.({
      pipeline,
      selectedBuckets: buckets, //selectedBuckets,
      selectedBucketKeys
    })
  }

  return (
    <div className={'flex flex-col gap-4 ' + classNames}>
      <GroupingPanel
        samples={samples}
        mode={'full'}
        initialPipeline={pipeline}
        onFieldsChange={(metadataFields) => {
          setMetadataFields(metadataFields)
          onFieldsChange?.(metadataFields)
        }}
        onBucketsChange={(buckets, pipeline) => {
          handleBucketsChange(buckets)
          setCurrentPipeline(pipeline)
        }}
        onInitialBuckets={(buckets, pipeline) => applyChanges(pipeline, selectedBuckets(buckets), selectedKeys)}
      />

      {allBuckets.length > 0 && (
        <div className="border-t pt-3">
          <h3 className="font-semibold mb-2" data-testid="groupingWrapperH3">
            Select Groups to Visualize:{' '}
            <button
              data-testid="groupingWrapperSelectAllBtn"
              className="btn btn-xs btn-outline mr-2"
              type="button"
              onClick={() => {
                const allKeys: Record<string, true> = {}
                allBuckets.forEach((b) => (allKeys[b.key] = true))
                setSelectedKeys(allKeys)
              }}
              disabled={allBuckets.length === 0 || Object.keys(selectedKeys ?? {}).length === allBuckets.length}
            >
              Select All ({allBuckets.length})
            </button>
            <button
              data-testid="groupingWrapperDeselectAllBtn"
              className="btn btn-xs btn-outline mr-2"
              type="button"
              onClick={() => setSelectedKeys({})}
              disabled={Object.keys(selectedKeys ?? {}).length === 0}
            >
              Deselect All
            </button>
          </h3>
          <div className="flex flex-col gap-1 max-h-48 overflow-y-auto">
            {allBuckets.map((bucket) => (
              <label key={bucket.key} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="checkbox checkbox-sm"
                  checked={!!selectedKeys[bucket.key]}
                  onChange={() => toggleKey(bucket.key)}
                />
                <span>
                  {bucket.label} ({bucket.samples.length})
                </span>
              </label>
            ))}
          </div>

          <button
            className="btn btn-sm btn-primary self-start mt-2"
            onClick={() => applyChanges(currentPipeline, selectedBuckets(allBuckets), selectedKeys)}
            disabled={!selectedKeys || Object.keys(selectedKeys).length === 0}
          >
            Apply
          </button>
        </div>
      )}
    </div>
  )
}
